import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const QuizDetails = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const quizid = localStorage.getItem("quizid");
  const [quizDetails, setQuizDetails] = useState({});
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [registrationStatus, setRegistrationStatus] = useState("close");
  const [apiSuccessMsg, setApiSuccessMsg] = useState("");

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        const response = await axiosPrivate.post(
          "/manager/quizdetails",
          JSON.stringify({
            quizid,
          })
        );

        console.log("response", response);
        setSuccess(true);
        setQuizDetails(response?.data?.quizDetails);
        setRegistrationStatus(
          response?.data?.quizDetails?.regstatus === "no" ? "close" : "open"
        );
      } catch (err) {
        console.error(err);
        setErrMsg(err?.response?.data?.message || err?.message);
      }
    };
    fetchQuizDetails();
  }, [axiosPrivate, quizid]);

  const handleRemoveParticipant = async () => {
    try {
      const confirmRemove = window.confirm(
        "Are you sure you want to remove the selected participant(s)?"
      );

      if (!confirmRemove) {
        return;
      }

      const users = selectedParticipants;
      const response = await axiosPrivate.post(
        "/manager/removeparticipant",
        JSON.stringify({
          quizid,
          users,
        })
      );

      setQuizDetails((prev) => ({
        ...prev,
        participants: prev.participants.filter(
          (participant) => !users.includes(participant.user)
        ),
      }));
      setSelectedParticipants([]);

      if (response.status === 200) {
        setApiSuccessMsg("Participants removed successfully");
      }
    } catch (err) {
      console.error(err);
      setErrMsg(err?.response?.data?.message || err?.message);
    }
  };

  const handleChangeRegistrationStatus = async () => {
    try {
      const confirmChange = window.confirm(
        "Are you sure you want to change the registration status of the quiz?"
      );
      if (!confirmChange) {
        return;
      }
      const status = registrationStatus === "close" ? "yes" : "no";
      const response = await axiosPrivate.post(
        "/manager/regstatus",
        JSON.stringify({
          quizid,
          status,
        })
      );

      setRegistrationStatus(status === "yes" ? "open" : "close");
      setQuizDetails((prev) => ({
        ...prev,
        regstatus: status,
      }));

      if (response.status === 200) {
        setApiSuccessMsg("Registration status updated successfully");
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        setErrMsg("Invalid status value or missing required fields.");
      } else if (err?.response?.status === 401) {
        setErrMsg("Invalid or missing authorization token.");
      } else if (err?.response?.status === 404) {
        setErrMsg("Quiz not found or unauthorized access.");
      } else {
        setErrMsg(
          "Failed to update registration status due to an internal error."
        );
      }
    }
  };

  const handleSendAllScores = async () => {
    try {
      const confirmChange = window.confirm(
        "Are you sure you want to send scores to all participants?"
      );
      if (!confirmChange) {
        return;
      }

      const response = await axiosPrivate.post(
        "/manager/sendallscores",
        JSON.stringify({
          quizid,
        })
      );

      if (response.status === 200) {
        setApiSuccessMsg(response.data.message);
      }
    } catch (err) {
      if (err?.response?.data?.error) {
        setErrMsg(err.response.data.error);
      } else {
        setErrMsg("Failed to send scores due to an internal error.");
      }
    }
  };

  return (
    <div className="fade-in" style={{ maxWidth: "1400px", margin: "0 auto", padding: "2rem 1rem" }}>
      {/* Back Button */}
      <button
        className="btn-secondary"
        style={{ marginBottom: "1.5rem", padding: "0.5rem 1rem", fontSize: "0.875rem" }}
        onClick={() => navigate("/manager")}
      >
        ← Back to Dashboard
      </button>

      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1
          style={{
            fontSize: "2.5rem",
            fontWeight: "700",
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Quiz Details
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: "1.125rem" }}>
          Manage participants and quiz settings
        </p>
      </div>

      {/* Messages */}
      {apiSuccessMsg && <p className="successmsg">{apiSuccessMsg}</p>}
      {errMsg && <p className="errmsg">{errMsg}</p>}

      {success && (
        <>
          {/* Quiz Information Card */}
          <div className="card" style={{ marginBottom: "2rem", padding: "2rem" }}>
            <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
              Quiz Information
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1.5rem" }}>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Quiz Code
                </p>
                <p style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--text-primary)" }}>
                  {quizDetails?.code}
                </p>
              </div>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Quiz Name
                </p>
                <p style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--text-primary)" }}>
                  {quizDetails?.quizname}
                </p>
              </div>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Quiz Type
                </p>
                <p style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--text-primary)" }}>
                  {quizDetails?.quiztype}
                </p>
              </div>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Credit Limit
                </p>
                <p style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--text-primary)" }}>
                  {quizDetails?.creditlimit}
                </p>
              </div>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Status
                </p>
                <span style={{
                  display: "inline-block",
                  padding: "0.25rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.875rem",
                  background: quizDetails?.status === "active" ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                  border: `1px solid ${quizDetails?.status === "active" ? "var(--success-green)" : "var(--error-red)"}`,
                  color: quizDetails?.status === "active" ? "var(--success-green)" : "var(--error-red)",
                  fontWeight: "600"
                }}>
                  {quizDetails?.status}
                </span>
              </div>
              <div>
                <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Registration
                </p>
                <span style={{
                  display: "inline-block",
                  padding: "0.25rem 0.75rem",
                  borderRadius: "9999px",
                  fontSize: "0.875rem",
                  background: registrationStatus === "open" ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                  border: `1px solid ${registrationStatus === "open" ? "var(--success-green)" : "var(--error-red)"}`,
                  color: registrationStatus === "open" ? "var(--success-green)" : "var(--error-red)",
                  fontWeight: "600"
                }}>
                  {registrationStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Participants Table */}
          {quizDetails?.participants?.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "1rem", color: "var(--text-primary)" }}>
                Participants ({quizDetails.participants.length})
              </h2>
              {selectedParticipants.length > 0 && (
                <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", background: "rgba(124, 58, 237, 0.1)", border: "1px solid var(--primary-purple)", borderRadius: "8px", fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  {selectedParticipants.length} participant(s) selected
                </div>
              )}
              <div style={{ overflowX: "auto", borderRadius: "16px", border: "1px solid var(--border)" }}>
                <table style={{ width: "100%", minWidth: "900px" }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Scores</th>
                      <th>Actions</th>
                      <th>Select</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizDetails?.participants?.map((participant) => (
                      <tr key={participant.user}>
                        <td style={{ fontWeight: "600", color: "var(--text-primary)" }}>{participant.name}</td>
                        <td>{participant.user}</td>
                        <td>
                          {participant.scores[0] === "NA" ? (
                            <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Not Attempted</span>
                          ) : (
                            <span style={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                              F={participant.scores[0]} A={participant.scores[1]} C={participant.scores[2]} E={participant.scores[3]}
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            {participant.scores[0] === "NA" ? (
                              <>
                                <button
                                  className="btn-primary"
                                  style={{ fontSize: "0.75rem", padding: "0.4rem 0.8rem", minWidth: "auto" }}
                                  onClick={() => {
                                    localStorage.setItem("user", participant?.user);
                                    navigate("/manager/viewparticipanturl");
                                  }}
                                >
                                  View URL
                                </button>
                                <button
                                  className="btn-secondary"
                                  style={{ fontSize: "0.75rem", padding: "0.4rem 0.8rem", minWidth: "auto" }}
                                  onClick={() => {
                                    localStorage.setItem("user", participant?.user);
                                    navigate("/manager/sendparticipanturl");
                                  }}
                                >
                                  Send URL
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn-primary"
                                  style={{ fontSize: "0.75rem", padding: "0.4rem 0.8rem", minWidth: "auto" }}
                                  onClick={() => {
                                    localStorage.setItem("user", participant?.user);
                                    navigate(`/manager/viewresponse?quizid=${quizid}&user=${participant?.user}`);
                                  }}
                                >
                                  View Response
                                </button>
                                <button
                                  className="btn-secondary"
                                  style={{ fontSize: "0.75rem", padding: "0.4rem 0.8rem", minWidth: "auto" }}
                                  onClick={() => {
                                    localStorage.setItem("user", participant?.user);
                                    navigate(`/manager/sendresponse?quizid=${quizid}&emails=${participant?.user}`);
                                  }}
                                >
                                  Send Response
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            onChange={() => {
                              const isSelected = selectedParticipants.includes(participant.user);
                              if (isSelected) {
                                setSelectedParticipants((prev) =>
                                  prev.filter((email) => email !== participant.user)
                                );
                              } else {
                                setSelectedParticipants((prev) => [...prev, participant.user]);
                              }
                            }}
                            checked={selectedParticipants.includes(participant.user)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="card" style={{ padding: "2rem" }}>
            <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
              Quiz Management
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem" }}>
              <button
                className="btn-primary"
                onClick={() => {
                  localStorage.setItem("quizid", quizid);
                  navigate("/manager/addparticipant");
                }}
              >
                ➕ Add Participant
              </button>

              {selectedParticipants.length > 0 && (
                <button className="btn-danger" onClick={handleRemoveParticipant}>
                  🗑️ Remove Selected ({selectedParticipants.length})
                </button>
              )}

              <button
                className="btn-secondary"
                onClick={() => {
                  localStorage.setItem("quizid", quizid);
                  navigate("/manager/setquizstatus");
                }}
              >
                ⚙️ Set Quiz Status
              </button>

              <button
                className="btn-secondary"
                onClick={handleChangeRegistrationStatus}
              >
                {registrationStatus === "open" ? "🔒 Close" : "🔓 Open"} Registration
              </button>

              <button className="btn-secondary" onClick={handleSendAllScores}>
                📧 Send All Scores
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuizDetails;
